<template>
  <!-- Hide tutorial completely in AR mode (from QR scan) -->
  <div
    v-if="!isARMode"
    class="studioTutorial"
    :class="{ tutorialFinished: !tutorialOpen }"
    role="complementary"
    :aria-label="tutorialOpen ? 'Design Studio Tutorial - Expanded' : 'Design Studio Tutorial - Collapsed'"
  >
    <button
      :aria-label="tutorialOpen ? 'Close tutorial' : 'Open tutorial'"
      :aria-expanded="tutorialOpen"
      class="closer"
      tabindex="0"
      @click="tutorialOpen = !tutorialOpen"
    >
      <span v-if="!tutorialOpen"><i class="fas fa-chevron-left" /></span>
      <span v-if="tutorialOpen"><i class="fas fa-chevron-right" /></span>
    </button>

    <div 
      class="tutorialBody" 
      tabindex="0"
      role="region"
      :aria-label="tutorialOpen ? 'Tutorial content' : 'Tutorial navigation'"
    >
      <h1 v-if="!tutorialOpen" class="sideText">Design Studio Tutorial</h1>

      <template v-if="tutorialOpen">
        <div class="tutorialTitle" role="heading" aria-level="1">Design Studio Tutorial</div>

        <div class="issues">
          If you encounter any issues, please email us at support@feltright.com
          or use our live chat.
        </div>
        <button
          class="primaryBTN"
          tabindex="0"
          aria-label="Open tutorial video in modal"
          @click="
            videoFinished = false;
            videoFullScreen = true;
          "
        >
          Watch Tutorial Video
        </button>

        <div class="tutorialSection">
          <div class="sectionTitle">
            <div class="label" role="heading" aria-level="2">Canvas Options</div>
          </div>
          <div class="copy">
            Explore the different canvas grid options. The grid you select will
            dictate the tiles you can use.
          </div>
          <div class="image">
            <img
              src="https://cdn.shopify.com/s/files/1/0091/0272/1104/files/Grid_Asset.png?v=1761793590"
              alt="Canvas Options showing different grid patterns available in the design studio"
            >
          </div>
        </div>
        <div class="tutorialSection">
          <div class="sectionTitle"><div class="label" role="heading" aria-level="2">Placing Tiles</div></div>
          <div class="copy">Step 1: Select the "Add" Tool.</div>
          <div class="image">
            <img
              src="https://cdn.shopify.com/s/files/1/0091/0272/1104/files/Add_Tile.png?v=1761793037"
              alt="Add Tool button highlighted in the toolbar interface"
            >
          </div>
          <div class="copy">Step 2: Select your tile shape.</div>
          <div class="image">
            <img
              src="https://cdn.shopify.com/s/files/1/0091/0272/1104/files/Tiles.png?v=1761793037"
              alt="Shape selection panel showing various tile shapes to choose from"
            >
          </div>
          <div class="copy">Step 3: Select your color.</div>
          <div class="image">
            <img
              src="https://cdn.shopify.com/s/files/1/0091/0272/1104/files/Color_6f1a598c-9cee-4ae9-84d7-0017ddee0e0a.png?v=1761793038"
              alt="Color selection palette showing available tile colors"
            >
          </div>
          <div class="copy">
            Step 4: Hover over the grid and click to place your tile.
          </div>
        </div>
        <div class="tutorialSection">
          <div class="sectionTitle">
            <div class="label" role="heading" aria-level="2">Selecting Tiles</div>
          </div>
          <div class="copy">
            Step 1: Click on any tile on the canvas to select it. A selected
            tile will have a dashed outline.
          </div>
          <div class="copy">
            Step 2: To avoid accidentally placing a tile while selecting you can
            use the "Select" tool.
          </div>
          <div class="image">
            <img
              src="https://cdn.shopify.com/s/files/1/0091/0272/1104/files/Select_Tile.png?v=1761793038"
              alt="Select tool and selected tiles demonstration showing dashed outline around selected tiles"
            >
          </div>
        </div>
        <div class="tutorialSection">
          <div class="sectionTitle">
            <div class="label" role="heading" aria-level="2">Navigating The Grid</div>
          </div>
          <div class="copy">
            <b>Option 1:</b> Select the "Move Canvas" tool, Click and drag
          </div>
          <img
            src="https://cdn.shopify.com/s/files/1/0091/0272/1104/files/Move_Grid.png?v=1761793037"
            alt="Move Canvas tool demonstration showing how to pan around the design canvas"
          >
          <div class="copy">
            <b>Option 2:</b> Hold <b>Space Bar</b>, click and drag
          </div>
        </div>
        <div class="tutorialSection">
          <div class="sectionTitle"><div class="label" role="heading" aria-level="2">Hotkeys</div></div>
          <div class="tourSequence" role="table" aria-label="Keyboard shortcuts for the design studio">
            <div class="step" role="row">
              <div class="info" role="cell">
                Deselect Tiles <br >
                <small
                  >(if tiles Selected) / Select tool (if no tiles
                  selected)</small
                >
              </div>
              <div class="label" role="cell">ESC</div>
            </div>
            <div class="step" role="row">
              <div class="info" role="cell">Place Tile Tool</div>
              <div class="label" role="cell">V</div>
            </div>
            <div class="step" role="row">
              <div class="info" role="cell">Save</div>
              <div class="label" role="cell">Ctrl (Win) / CMD (Mac) + s</div>
            </div>
            <div class="step" role="row">
              <div class="info" role="cell">Undo</div>
              <div class="label" role="cell">Ctrl (Win) / CMD (Mac) + z</div>
            </div>
            <div class="step" role="row">
              <div class="info" role="cell">Redo</div>
              <div class="label" role="cell">Ctrl (Win) / CMD (Mac) + y</div>
            </div>
            <div class="step" role="row">
              <div class="info" role="cell">Panning / Move Canvas</div>
              <div class="label" role="cell">Space</div>
            </div>
            <div class="step" role="row">
              <div class="info" role="cell">Delete Tile</div>
              <div class="label" role="cell">Del</div>
            </div>
            <div class="step" role="row">
              <div class="info" role="cell">Zoom in / out</div>
              <div class="label" role="cell">Ctrl (Win) / CMD (Mac) + Mouse Scroll</div>
            </div>
            <div class="clear" style="height: 25px" />
          </div>
        </div>
      </template>
    </div>

    <Teleport to="body">
      <div
        v-if="!videoFinished"
        class="studioVideoTutorial animated fadeIn"
        :class="{'fullscreen': videoFullScreen}"
        role="dialog"
        aria-modal="true"
        aria-labelledby="video-modal-title"
        tabindex="-1"
      >
        <div class="heading">
          <div id="video-modal-title" class="label">Design Studio Tutorial</div>
          <div class="buttons">
            <button 
              class="mr-4"
              @click="videoFullScreen = !videoFullScreen"
              :aria-label="videoFullScreen ? 'Exit fullscreen' : 'Enter fullscreen'"
              tabindex="0"
            >
              <i class="fal fa-expand"/>
            </button>
            <button 
              @click="setCookie('videoFinished', true); videoFinished = true;"
              aria-label="Close video tutorial"
              tabindex="0"
            >
              <i class="fal fa-times"/>
            </button>
          </div>
        </div>
        <div class="video">
          <iframe
            width="560"
            height="315"
            src="https://www.youtube.com/embed/lbM0KiMhhV8?start=352"
            title="Design Studio Tutorial Video - Learn how to use the Felt Right design studio"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen="allowfullscreen"
          />
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
const route = useRoute()

// Hide tutorial completely when ar=true (AR mode from QR scan)
const isARMode = computed(() => route.query.ar === 'true')

const tutorialOpen = ref(false);
const videoFinished = ref(useCookie('videoFinished').value || false);
const videoFullScreen = ref(false);
</script>

<style lang="scss" scoped>
@mixin maxWidth($width) {
  @media (max-width: $width) {
    @content;
  }
}

.studioTutorial {
  --tutorial-scrollbar-overlay-padding: 24px;

  display: flex;
  width: 350px;
  position: fixed;
  top: 206px;
  right: 0;
  z-index: 90;

  @include maxWidth(1000px) {
		display: none;
	}

  .tutorialBody {
    flex: 1;
    height: calc(100vh - 206px);
    background: #e5e5e5;
    overflow-y: auto;
    padding: 20px var(--tutorial-scrollbar-overlay-padding) 20px 20px;
    color: #454444;

    .sideText {
      writing-mode: vertical-rl;
      color: #000;
      font-weight: bold;
      font-size: 18px;
      padding: 0;
      margin: 0;
    }
  }

  &.tutorialFinished {
    width: calc(60px + var(--tutorial-scrollbar-overlay-padding));

    .tutorialBody {
      flex: 0 0 auto;
      width: calc(30px + var(--tutorial-scrollbar-overlay-padding));
      padding: 25px var(--tutorial-scrollbar-overlay-padding) 25px 5px;
    }
    .closer {
      background: #4294f7;
      color: #fff;
    }
  }

  .closer {
    flex: 0 0 auto;
    position: relative;
    top: 50px;
    background: #e5e5e5;
    font-size: 35px;
    color: #000;
    border-radius: 5px 0 0 5px;
    z-index: 100;
    width: 30px;
    height: 40px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .tutorialTitle {
    font-size: 22px;
    margin: 0 0 10px 0;
    padding: 0 0 10px 0;
    border-bottom: 1px solid #000;
    color: #000;
  }
  .issues {
    font-size: 12px;
    margin: 15px 0;
  }
  .tutorialSection {
    .sectionTitle {
      background: #d8d9d9;
      font-weight: bold;
      font-size: 18px;
      margin: 25px -20px 0 -20px;
      padding: 5px 20px;
    }
    .copy {
      font-size: 15px;
      margin: 25px 0 10px 0;
    }
  }

  .tourSequence {
    margin: 0;
  }
}

.tourSequence {
  display: flex;
  flex-direction: column;
  margin: -40px -40px -40px 0;

  b {
    color: #1b93ff;
  }

  .step {
    margin: 18px;

    .label {
      font-weight: bold;
      font-size: 16px;
      color: #1b93ff;
    }
  }

  .tourTop {
    background: #f9f9f9;
    flex: 1;
    border-bottom: 1px solid #707070;
    display: flex;
    width: 100%;
    height: 100%;
    justify-content: center;
    align-items: center;
    padding: 80px 0;

    &.noPadding {
      padding: 0;
    }
  }
  .tourBottom {
    flex: 0 0 auto;
    padding: 15px;

    .label {
      font-weight: bold;
      margin: 0 0 5px 0;
    }

    .buttons {
      justify-content: flex-end;
    }
  }
}

.primaryBTN {
  text-decoration: none;
  letter-spacing: 0.5px;
  background-color: #000;
  color: #fff;
  font-size: 14px;
  letter-spacing: 0px;
  text-align: center;
  padding: 8px 24px;
  font-weight: 600;
  border: 2px solid #000;
  border-radius: 0;
  cursor: pointer;
  display: inline-block;
  -webkit-appearance: none;
  transition: all 0.3s;
}

.studioVideoTutorial {
	position: fixed;
	bottom: -5px;
	right: 20%;
	box-shadow: 0 -3px 8px rgba(0,0,0,0.2);
	width: 440px;
	max-width: 90%;
	z-index: 99999;
  background: none;
	border-radius: 5px 5px 0 0;

  @include maxWidth(600px) {
		right: unset;
		left: 50%;
		margin-left: -220px;
	}

	@include maxWidth(470px) {
		margin: 0 auto;
		left: 5%;
	}

	&.fullscreen {
		left: 5vw;
		top: 0;
		bottom: unset;
		width: 90vw;
		height: 90vh;
		margin: 0 auto;
		margin-top: 5vh;

		iframe {
			height: 88vh;
		}
	}

	.heading {
		background: #666;
		border-radius: 5px 5px 0 0;
		color: #fff;
		font-size: 12px;
		display: flex;
		align-items: center;
		padding: 10px;
		font-weight: 600;

		.label {
			flex: 1;
		}

		.buttons {
			flex: 0 0 auto;

			a {
				color: #fff;
				display: inline-block;
				margin: 0 10px;
			}
		}
	}

	iframe {
		width: 100%;
	}
}
</style>
